import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Spinner, Button, Modal } from 'react-bootstrap';
import AdminSidebar2 from '../components/AdminSidebar2';
import AdminNavbar2 from '../components/AdminNavbar2';
import { useSidebar } from '../components/SidebarContext';
import VisibilityIcon from '@mui/icons-material/Visibility';

function AdminStudentList() {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [adminCollege, setAdminCollege] = useState('');
  const studentsPerPage = 100;

  const { isSidebarOpen } = useSidebar();
  const sidebarWidth = isSidebarOpen ? '15.6rem' : '4.4rem';

  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo(0, 0);
    }
  }, [currentPage]);

  // Get admin college from localStorage on component mount
  useEffect(() => {
    const adminUser = localStorage.getItem('collegeAdminUser');
    if (adminUser) {
      try {
        const parsedAdmin = JSON.parse(adminUser);
        const collegeName = parsedAdmin.collegeName || parsedAdmin.college || '';
        setAdminCollege(collegeName);
        console.log('Admin college:', collegeName);
      } catch (error) {
        console.error('Error parsing admin user from localStorage:', error);
        setError('Unable to retrieve admin college information');
      }
    } else {
      setError('Admin not logged in. Please login again.');
    }
  }, []);

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      minHeight: '100vh',
      background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
      fontFamily: "'Roboto', sans-serif",
    },
    sidebar: {
      width: sidebarWidth,
      borderRadius: '30px',
      height: '100vh',
      flexShrink: 0,
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: '10',
      transition: 'width 0.3s ease',
    },
    content: {
      flex: 1,
      padding: '15px',
      margin: '6rem 2rem 2rem 2rem',
      backgroundColor: 'white',
      transition: 'margin-left 0.3s ease',
      minHeight: 'calc(100vh - 8rem)',
      maxHeight: 'calc(100vh - 8rem)',
      borderTopLeftRadius: '1rem',
      borderTopRightRadius: '1rem',
      borderBottomLeftRadius: '1rem',
      borderBottomRightRadius: '1rem',
      maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    alert: {
      backgroundColor: '#F3E8FF',
      color: '#6B21A8',
      padding: '10px',
      borderRadius: '8px',
      marginBottom: '12px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      maxWidth: '500px',
      animation: 'slideInFromRight 0.5s ease-out',
      fontFamily: "'Roboto', sans-serif",
    },
    headerContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      width: '100%',
      padding: '0 10px 0 0',
      position: 'relative',
      zIndex: 999,
    },
    searchFilterGroup: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '4px',
      position: 'relative',
      marginLeft: '20px', // Moved search bar to the right
    },
    filterMessageGroup: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '8px',
    },
    noStudentsMessage: {
      color: '#6c757d',
      fontStyle: 'italic',
      fontSize: '12px',
      margin: 0,
    },
    collegeInfo: {
      color: '#6B21A8',
      fontSize: '14px',
      fontWeight: '500',
      fontFamily: "'Roboto', sans-serif",
      marginBottom: '8px',
    },
    searchInput: {
      width: '80%',
      maxWidth: '450px',
      padding: '6px 12px',
      borderRadius: '30px',
      border: '1px solid #ddd',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.3s ease',
      position: 'relative',
      zIndex: 1,
    },
    viewModalHeader: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#6B21A8',
      marginBottom: '20px',
      fontFamily: "'Roboto', sans-serif",
      position: 'sticky',
      top: 0,
      background: '#ffffff',
      zIndex: 10,
      padding: '10px 1rem',
      width: '100%',
      boxSizing: 'border-box',
    },
    viewModalLabel: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#6B21A8',
      width: '120px',
      flexShrink: 0,
      fontFamily: "'Roboto', sans-serif",
    },
    modalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '0.75rem',
      fontSize: '0.875rem',
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
      padding: '1rem',
      width: '95%',
      maxHeight: '65vh',
      boxSizing: 'border-box',
      overflowY: 'auto',
      overflowX: 'hidden',
      position: 'relative',
      fontFamily: "'Roboto', sans-serif",
    },
    modalFooter: {
      position: 'sticky',
      bottom: 0,
      background: '#ffffff',
      zIndex: 10,
      padding: '5px 0',
      borderTop: '1px solid #ddd',
      width: '100%',
      boxSizing: 'border-box',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '0.875rem',
      padding: '5px 15px',
      cursor: 'pointer',
      color: '#6B21A8',
    },
    modalViewDetails: {
      fontSize: '0.875rem',
    },
    detailRow: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: '15px',
    },
    colon: {
      width: '10px',
      textAlign: 'center',
      flexShrink: 0,
    },
    contentWrapper: {
      flex: 1,
      wordWrap: 'break-word',
      fontFamily: "'Roboto', sans-serif",
    },
    tableContainer: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'auto',
      borderRadius: '0.5rem',
      maxHeight: 'calc(97vh - 12rem)',
      minHeight: '300px',
      width: '100%',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "'Roboto', sans-serif",
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      border: 'none',
      fontSize: '12px',
      fontFamily: "'Roboto', sans-serif",
    },
    paginationContainer: {
      background: '#ffffff',
      padding: '10px 0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: '40px',
      marginTop: '20px',
      zIndex: 5,
      fontFamily: "'Roboto', sans-serif",
    },
    pageButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3px 13px',
      fontSize: '13px',
      fontWeight: '600',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      fontFamily: "'Roboto', sans-serif",
      flexShrink: '0',
      minWidth: '80px',
      textAlign: 'center',
      transition: 'transform 0.2s ease, background 0.3s ease, color 0.3s ease',
    },
    pageButtonPrevious: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3px 13px',
      fontSize: '13px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      fontFamily: "'Roboto', sans-serif",
      flexShrink: '0',
      minWidth: '80px',
      textAlign: 'center',
      transition: 'transform 0.2s ease, background 0.3s ease, color 0.3s ease',
      color: currentPage === 1 ? '#aaa' : '#6B21A8',
    },
    pageNumberButton: {
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '13px',
      fontWeight: '600',
      color: '#333',
      cursor: 'pointer',
      borderRadius: '50%',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      fontFamily: "'Roboto', sans-serif",
      transition: 'transform 0.2s ease',
    },
    paginationWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    customDropdown: {
      width: '60px',
      padding: '5px 25px 5px 10px',
      borderRadius: '5px',
      border: '1px solid #ccc',
      fontSize: '14px',
      color: '#1F2937',
      cursor: 'pointer',
      backgroundColor: '#fff',
      appearance: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236B21A8%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000%20.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2010-1.414-1.414z%22%2F%3E%3C%2Fsvg%3E")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 10px center',
      backgroundSize: '12px',
      transition: 'border-color 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
      position: 'relative',
    },
    customDropdownOptions: {
      position: 'absolute',
      bottom: '100%',
      left: 0,
      width: '60px',
      border: '1px solid #ccc',
      borderRadius: '5px',
      backgroundColor: '#fff',
      zIndex: 1000,
      maxHeight: '200px',
      overflowY: 'auto',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      fontFamily: "'Roboto', sans-serif",
    },
    customOption: {
      padding: '5px 10px',
      cursor: 'pointer',
      fontFamily: "'Roboto', sans-serif",
    },
    modalCloseButton: {
      backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      border: '2px solid transparent',
      borderRadius: '30px',
      padding: '5px 15px',
      fontSize: '0.875rem',
      fontWeight: 'bold',
      color: '#6B21A8',
      cursor: 'pointer',
      transition: 'all 0.4s ease, background 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
  };

  const fetchAllStudents = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!adminCollege) {
        setError('Admin college information not found. Please login again.');
        return;
      }

      // Modified API call to include college filter as query parameter
      const response = await axios.get(`https://exam.doode.in/api/student/studentlist?collegeName=${encodeURIComponent(adminCollege)}`, {
        withCredentials: true,
      });

      console.log("API Response:", response.data);

      if (!Array.isArray(response.data)) {
        throw new Error("Expected array of students but got different data structure");
      }

      if (response.data.length > 0) {
        console.log("Sample student object:", response.data[0]);
      }

      // Additional client-side filtering as a safety measure
      const filteredByCollege = response.data.filter(student => 
        student.collegeName && student.collegeName.toLowerCase() === adminCollege.toLowerCase()
      );

      const formattedStudents = filteredByCollege.map((student) => {
        const createdAtDate = student.createdAt ? new Date(student.createdAt) : null;
        const updatedAtDate = student.updatedAt ? new Date(student.updatedAt) : null;
        return {
          id: student._id,
          username: student.username || '',
          fullName: student.fullName || '',
          email: student.email || '',
          admissionNo: student.admissionNo || 'N/A',
          collegeName: student.collegeName || 'N/A',
          createdAt: createdAtDate,
          updatedAt: updatedAtDate,
          accountType: student.accountType || 'unknown',
        };
      });

      setStudents(formattedStudents);
      setFilteredStudents(formattedStudents);
      // showNotification(`Found ${formattedStudents.length} students from ${adminCollege}`);
    } catch (error) {
      console.error('Error fetching students:', error);
      let errorMessage = "Failed to load students";

      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = "Unauthorized - Please login as admin";
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = `Server error: ${error.response.status}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-dropdown') && isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Fetch students only when adminCollege is available
  useEffect(() => {
    if (adminCollege) {
      fetchAllStudents();
    }
  }, [adminCollege]);

  const handleSortAndFilter = () => {
    let updatedStudents = [...students];

    if (searchTerm.trim()) {
      updatedStudents = updatedStudents.filter((student) => {
        const fullName = (student.fullName || '').toLowerCase();
        const email = (student.email || '').toLowerCase();
        const admissionNo = (student.admissionNo || '').toLowerCase();
        const collegeName = (student.collegeName || '').toLowerCase();
        const accountType = (student.accountType || '').toLowerCase();
        const search = searchTerm.toLowerCase();

        return (
          fullName.includes(search) ||
          email.includes(search) ||
          admissionNo.includes(search) ||
          collegeName.includes(search) ||
          accountType.includes(search)
        );
      });
    }

    if (sortOption === 'adminCreated') {
      updatedStudents = updatedStudents.filter((student) => student.accountType === 'adminCreated');
    } else if (sortOption === 'portalCreated') {
      updatedStudents = updatedStudents.filter((student) => student.accountType === 'portalCreated');
    }

    updatedStudents.sort((a, b) => {
      const dateA = a.createdAt instanceof Date && !isNaN(a.createdAt.getTime()) ? a.createdAt.getTime() : 0;
      const dateB = b.createdAt instanceof Date && !isNaN(b.createdAt.getTime()) ? b.createdAt.getTime() : 0;
      return dateB - dateA;
    });

    setFilteredStudents(updatedStudents);
    setCurrentPage(1);
  };

  useEffect(() => {
    handleSortAndFilter();
  }, [sortOption, students, searchTerm]);

  useEffect(() => {
    if (notifications.length > 0) {
      const timers = notifications.map((notification, index) =>
        setTimeout(() => {
          setNotifications((prev) => prev.filter((_, i) => i !== index));
        }, 5000)
      );
      return () => timers.forEach((timer) => clearTimeout(timer));
    }
  }, [notifications]);

  const showNotification = (message) => {
    const newNotification = { message, id: Date.now() };
    setNotifications((prev) => [...prev, newNotification]);
  };

  const handleCloseNotification = (id) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  };

  const handleSortSelect = (option) => {
    setSortOption(option);
    document.activeElement.blur();
  };

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
  };

  const handleCloseError = () => {
    setError(null);
  };

  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return 'Not Available';
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatModalDate = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return 'Not Available';
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirstStudent, indexOfLastStudent);
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);

  const paginate = (pageNumber) => {
    const maxPage = Math.min(pageNumber, totalPages);
    setCurrentPage(maxPage > 0 ? maxPage : 1);
    setIsDropdownOpen(false);
  };

  const pageRange = Array.from({ length: totalPages }, (_, i) => i + 1);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.sidebar}>
          <AdminSidebar2 />
        </div>
        <div style={{ marginLeft: sidebarWidth, width: `calc(100% - ${sidebarWidth})`, transition: 'margin-left 0.3s ease' }}>
          <AdminNavbar2 />
          <div style={styles.content}>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p style={{ marginTop: '10px', fontFamily: "'Roboto', sans-serif" }}>Loading student data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        .search-input:focus {
          border-color: #1890ff !important;
        }

        @keyframes glow {
          0% {
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
          }
          50% {
            box-shadow: 0 0 15px rgba(107, 33, 168, 0.8);
          }
          100% {
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
          }
        }

        @keyframes slideInFromRight {
          0% {
            transform: translateX(100%);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .custom-table {
          border: none !important;
          table-layout: fixed;
          width: 100%;
          min-width: 100%;
        }

        .custom-table th,
        .custom-table td {
          border: none !important;
        }

        .custom-table thead {
          background: linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF);
          position: 'sticky';
          top: 0;
          zIndex: 10;
        }

        .custom-table tbody tr:nth-child(odd) {
          background-color: #FFFFFF;
        }

        .custom-table tbody tr:nth-child(even) {
          background: linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB);
        }

        .custom-table tbody tr:hover {
          background: linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5);
        }

        .modal-content {
          background: ${styles.modalContent.backgroundColor} !important;
          border-radius: ${styles.modalContent.borderRadius} !important;
          box-shadow: ${styles.modalContent.boxShadow} !important;
          padding: ${styles.modalContent.padding} !important;
          width: ${styles.modalContent.width} !important;
          max-height: ${styles.modalContent.maxHeight} !important;
          box-sizing: ${styles.modalContent.boxSizing} !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          margin: 0 auto !important;
          font-family: 'Roboto', sans-serif !important;
          font-size: ${styles.modalContent.fontSize} !important;
        }

        .modal-header,
        .modal-body,
        .modal-footer {
          background: transparent !important;
          border: none !important;
        }

        .modal-header {
          position: sticky;
          top: 0;
          z-index: 10;
          background: ${styles.modalContent.backgroundColor} !important;
          padding: 0 !important;
          width: 100%;
          box-sizing: border-box;
        }

        .modal-footer {
          position: sticky;
          bottom: 0;
          z-index: 10;
          background: ${styles.modalContent.backgroundColor} !important;
          padding: 5px 0 !important;
          border-top: 1px solid #ddd !important;
          width: 100%;
          box-sizing: border-box;
        }

        @media (max-width: 850px) {
          .modal-content {
            width: 90% !important;
          }
        }

        .modal-content::-webkit-scrollbar {
          width: 8px;
        }
        .modal-content::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .modal-content::-webkit-scrollbar-thumb {
          background: #8f8f8f;
          border-radius: 4px;
        }
        .modal-content::-webkit-scrollbar-thumb:hover {
          background: #8f8f8f;
        }

        .table-container {
          position: relative;
          overflow-y: auto;
          overflow-x: hidden !important;
          max-height: calc(95vh - 12rem);
          display: flex;
          flex-direction: column;
        }

        .pagination-button:hover:not(:disabled) {
          background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
          transform: scale(1.05);
          animation: glow 1.5s ease-in-out infinite;
        }

        .pagination-button:active:not(:disabled) {
          transform: scale(0.95);
        }

        .custom-dropdown {
          position: relative;
          width: 60px;
          padding: 5px 25px 5px 10px;
          border: 1px solid #ccc;
          border-radius: 5px;
          background-color: #fff;
          color: #1F2937;
          cursor: 'pointer';
          font-family: 'Roboto', sans-serif !important;
          backgroundImage: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236B21A8%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000%20.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2010-1.414-1.414z%22%2F%3E%3C%2Fsvg%3E");
          backgroundRepeat: no-repeat;
          backgroundPosition: right 10px center;
          backgroundSize: 12px;
        }

        .custom-dropdown-options {
          position: absolute;
          bottom: 100%;
          left: 0;
          width: 60px;
          border: 1px solid #ccc;
          border-radius: 5px;
          background-color: #fff;
          z-index: 1000;
          max-height: 200px;
          overflow-y: auto;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .custom-option {
          padding: 5px 10px;
          cursor: pointer;
        }

        .custom-option:hover {
          background: #D3D3D3 !important;
          color: #1F2937 !important;
        }

        .custom-dropdown-options::-webkit-scrollbar {
          width: 8px;
        }
        .custom-dropdown-options::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .custom-dropdown-options::-webkit-scrollbar-thumb {
          background: #8f8f8f;
          border-radius: 4px;
        }
        .custom-dropdown-options::-webkit-scrollbar-thumb:hover {
          background: #6B21A8;
        }

        .page-select:focus {
          border-color: #6B21A8;
          outline: none;
          box-shadow: 0 0 5px rgba(107, 33, 168, 0.3);
        }

        .modal-close-button:hover:not(:disabled) {
          background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
          transform: scale(1.03);
          animation: glow 1.5s ease-in-out infinite;
        }

        .pagination-button:disabled {
          background-color: #ccc !important;
          color: #aaa !important;
          cursor: not-allowed;
        }
      `}</style>

      <div style={styles.sidebar}>
        <AdminSidebar2 />
      </div>
      <div style={{ marginLeft: sidebarWidth, width: `calc(100% - ${sidebarWidth})`, transition: 'margin-left 0.3s ease' }}>
        <AdminNavbar2 />
        <div style={styles.content}>
          <div style={{ width: '100%' }}>
            <div style={styles.headerContainer}>
              <div style={{ flex: 1 }}></div>
              <div style={styles.filterMessageGroup}>
                
                <div style={styles.searchFilterGroup}>
                  <input
                    type="text"
                    placeholder="Search students..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                    className="search-input"
                    aria-label="Search students"
                  />
                </div>
                {filteredStudents.length === 0 && (
                  <p style={styles.noStudentsMessage}>
                    {sortOption === 'adminCreated'
                      ? 'No admin-created students found'
                      : sortOption === 'portalCreated'
                      ? 'No portal-created students found'
                      : 'No students registered yet'}
                  </p>
                )}
              </div>
            </div>

            {error && (
              <div style={{ ...styles.alert, alignSelf: 'center' }}>
                <div>
                  <strong style={{ fontSize: '14px', fontFamily: "'Roboto', sans-serif" }}>Error Loading Students</strong>
                  <p style={{ margin: '5px 0', fontFamily: "'Roboto', sans-serif" }}>{error}</p>
                  <div>
                    <p style={{ fontWeight: '500', marginBottom: '5px', fontFamily: "'Roboto', sans-serif" }}>Troubleshooting steps:</p>
                    <ul style={{ marginBottom: 0, paddingLeft: '20px', fontFamily: "'Roboto', sans-serif" }}>
                      <li>Ensure you're logged in as admin</li>
                      <li>Check your internet connection</li>
                      <li>Verify the API endpoint is correct</li>
                    </ul>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Button
                    variant="outline-danger"
                    onClick={fetchAllStudents}
                    style={{
                      borderWidth: '2px',
                      fontWeight: '500',
                      padding: '5px 15px',
                      fontSize: '12px',
                      borderColor: '#6B21A8',
                      color: '#6B21A8',
                      fontFamily: "'Roboto', sans-serif",
                    }}
                  >
                    Retry
                  </Button>
                  <button style={styles.closeButton} onClick={handleCloseError} aria-label="Close">
                    ×
                  </button>
                </div>
              </div>
            )}

            {notifications.map((notification) => (
              <div key={notification.id} style={{ ...styles.alert, alignSelf: 'center' }}>
                <span style={{ fontSize: '14px', fontFamily: "'Roboto', sans-serif" }}>{notification.message}</span>
                <button
                  style={styles.closeButton}
                  onClick={() => handleCloseNotification(notification.id)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            ))}

            <div style={styles.tableContainer} className="table-container" ref={tableContainerRef}>
              <table
                className="custom-table"
                style={{
                  borderRadius: '8px',
                  marginBottom: 0,
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '12px',
                  border: 'none',
                  tableLayout: 'fixed',
                }}
              >
                <thead
                  style={{
                    background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  <tr>
                    <th style={{ padding: '8px 10px', fontWeight: '500', textAlign: 'center', color: '#6B21A8', width: '5%' }}>
                      Sr.
                    </th>
                    <th style={{ padding: '8px 10px', fontWeight: '500', textAlign: 'center', color: '#6B21A8', width: '20%' }}>
                      Full Name
                    </th>
                    <th style={{ padding: '8px 10px', fontWeight: '500', textAlign: 'center', color: '#6B21A8', width: '25%' }}>
                      Email
                    </th>
                    <th style={{ padding: '8px 10px', fontWeight: '500', textAlign: 'center', color: '#6B21A8', width: '15%' }}>
                      Admission No
                    </th>
                    <th style={{ padding: '8px 10px', fontWeight: '500', textAlign: 'center', color: '#6B21A8', width: '20%' }}>
                      College Name
                    </th>
                    <th style={{ padding: '8px 10px', fontWeight: '500', textAlign: 'center', color: '#6B21A8', width: '10%' }}>
                      Account Type
                    </th>
                    <th style={{ padding: '8px 10px', fontWeight: '500', textAlign: 'center', color: '#6B21A8', width: '5%' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentStudents.length > 0 && currentStudents.map((student, index) => (
                    <tr key={student.id}>
                      <td
                        style={{
                          padding: '10px 12px',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          color: '#333',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {indexOfFirstStudent + index + 1}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          verticalAlign: 'middle',
                          textAlign: 'left',
                          color: '#333',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {student.fullName}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          color: '#333',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {student.email}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          color: '#333',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {student.admissionNo}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          color: '#333',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {student.collegeName}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          color: '#333',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {student.accountType}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          color: '#333',
                        }}
                      >
                        <button
                          onClick={() => handleViewStudent(student)}
                          style={{
                            backgroundColor: 'transparent',
                            color: '#cc8888',
                            border: 'none',
                            borderRadius: '2px',
                            padding: '2px',
                            cursor: 'pointer',
                            transition: 'color 0.3s ease, transform 0.3s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = '#b36666';
                            e.target.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = '#cc8888';
                            e.target.style.transform = 'scale(1)';
                          }}
                          title="View student details"
                        >
                          <VisibilityIcon style={{ fontSize: '17px' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={styles.paginationContainer}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Roboto', sans-serif" }}>
                  <span style={{ fontSize: '14px', color: '#1F2937', fontFamily: "'Roboto', sans-serif" }}>
                    Showing {indexOfFirstStudent + 1} to {Math.min(indexOfLastStudent, filteredStudents.length)} of {filteredStudents.length} entries
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Roboto', sans-serif" }}>
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      ...styles.pageButtonPrevious,
                      backgroundColor: currentPage === 1 ? '#ccc' : undefined,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    }}
                    className="pagination-button"
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  <div style={styles.paginationWrapper}>
                    {Array.from({ length: totalPages }, (_, i) => {
                      const pageNum = i + 1;
                      const shouldShow = 
                        pageNum === 1 || 
                        pageNum === totalPages || 
                        (pageNum >= currentPage - 2 && pageNum <= currentPage + 2);
                      
                      if (!shouldShow) {
                        if (
                          (pageNum === currentPage - 3 && currentPage > 4) || 
                          (pageNum === currentPage + 3 && currentPage < totalPages - 3)
                        ) {
                          return (
                            <span key={`ellipsis-${pageNum}`} style={{ padding: '0 5px', color: '#6B21A8', fontFamily: "'Roboto', sans-serif" }}>
                              ...
                            </span>
                          );
                        }
                        return null;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          style={{
                            ...styles.pageNumberButton,
                            background: currentPage === pageNum ? 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)' : '#ffffff',
                            color: currentPage === pageNum ? '#6B21A8' : '#333',
                          }}
                          className={`pagination-button page-number-button ${currentPage === pageNum ? 'active' : ''}`}
                          aria-label={`Page ${pageNum}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      ...styles.pageButton,
                      backgroundColor: currentPage === totalPages ? '#ccc' : undefined,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    }}
                    className="pagination-button"
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Roboto', sans-serif", position: 'relative' }}>
                  <div
                    className="custom-dropdown"
                    style={styles.customDropdown}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    {currentPage}
                    {isDropdownOpen && (
                      <div className="custom-dropdown-options" style={styles.customDropdownOptions}>
                        {pageRange.map((pageNum) => (
                          <div
                            key={pageNum}
                            className="custom-option"
                            style={styles.customOption}
                            onClick={() => {
                              paginate(pageNum);
                              setIsDropdownOpen(false);
                            }}
                          >
                            {pageNum}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        backdrop="static"
        contentClassName="modal-content"
      >
        <Modal.Header style={styles.viewModalHeader}>
          <Modal.Title>Student Details</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '0 0 10px 0' }}>
          {selectedStudent && (
            <>
              <div style={styles.detailRow}>
                <strong style={styles.viewModalLabel}>Username</strong>
                <span style={styles.colon}>:</span>
                <span style={styles.contentWrapper}>{selectedStudent.username}</span>
              </div>
              <div style={styles.detailRow}>
                <strong style={styles.viewModalLabel}>Full Name</strong>
                <span style={styles.colon}>:</span>
                <span style={styles.contentWrapper}>{selectedStudent.fullName}</span>
              </div>
              <div style={styles.detailRow}>
                <strong style={styles.viewModalLabel}>Email</strong>
                <span style={styles.colon}>:</span>
                <span style={styles.contentWrapper}>{selectedStudent.email}</span>
              </div>
              <div style={styles.detailRow}>
                <strong style={styles.viewModalLabel}>Admission No</strong>
                <span style={styles.colon}>:</span>
                <span style={styles.contentWrapper}>{selectedStudent.admissionNo}</span>
              </div>
              <div style={styles.detailRow}>
                <strong style={styles.viewModalLabel}>College Name</strong>
                <span style={styles.colon}>:</span>
                <span style={styles.contentWrapper}>{selectedStudent.collegeName}</span>
              </div>
              <div style={styles.detailRow}>
                <strong style={styles.viewModalLabel}>Account Type</strong>
                <span style={styles.colon}>:</span>
                <span style={styles.contentWrapper}>{selectedStudent.accountType}</span>
              </div>
              <div style={styles.detailRow}>
                <strong style={styles.viewModalLabel}>Registered</strong>
                <span style={styles.colon}>:</span>
                <span style={styles.contentWrapper}>{formatModalDate(selectedStudent.createdAt)}</span>
              </div>
              <div style={styles.detailRow}>
                <strong style={styles.viewModalLabel}>Last Updated</strong>
                <span style={styles.colon}>:</span>
                <span style={styles.contentWrapper}>{formatModalDate(selectedStudent.updatedAt)}</span>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={styles.modalFooter}>
          <Button
            variant="secondary"
            onClick={handleCloseModal}
            style={styles.modalCloseButton}
            className="modal-close-button"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AdminStudentList;